import { useState, React, useEffect } from "react";
import Copyright from "../../components/LoginComponents/Copyright";
import {
  Box,
  Grid,
  Typography,
  Container,
  Button,
  TextField, Link,
} from "@mui/material";
import { successNote, toastErrorNotify } from "../../helpers/toastNotify";
import Loading from "../../layout/Loading";
import { SendPhoneValidationOTP, CheckInvitationStatus, ResendExpiredInvitation } from "../../api/endpoints";
import apiClient from "../../api/apiClient";
import { useNavigate, useSearchParams} from "react-router-dom";
import * as Yup from "yup";

// Invitation links used to be accepted at face value here with no server-side check at all - a
// bad link (expired, superseded by a later resend, or genuinely invalid) would only fail later,
// deep in the phone/OTP steps, with a generic toast that didn't explain what was wrong or offer any
// way to recover other than asking MV/an admin to delete and re-invite the admin from scratch.
// This checks status up front and branches to the right state before showing any form.
const INVITATION_STATUS = {
  LOADING: "loading",
  VALID: "Valid",
  EXPIRED: "Expired",
  // This link's registration key isn't the admin's current one - a later invite/resend already
  // replaced it (e.g. they deleted their newest invitation email and used an older one still
  // sitting in their inbox). Same recovery as EXPIRED: a "get a new invitation" button.
  SUPERSEDED: "Superseded",
  INVALID: "Invalid",
  // Not a CheckInvitationStatus value - set client-side when SendPhoneValidationOTP itself reports
  // the SMS attempt cap is used up (backend's PhoneOTPUsageRight <= 0 check, which CheckInvitationStatus
  // has no visibility into up front since it only runs once on page load, before any SMS is sent).
  // Recovery is the same "request a new invitation" button as EXPIRED - ResendExpiredInvitation resets
  // the attempt count on a fresh send.
  LIMIT_REACHED: "LimitReached",
};

// Exact text of the backend's PhoneOTPUsageRight <= 0 message (OrgAdminUserAuthManager
// .SendPhoneValidationOTP) - matched here so it never reaches the Norwegian UI as raw English.
const OTP_LIMIT_REACHED_MESSAGE =
  "Please contact MakingVeiw AS. Your registration attempt has surpassed the general count.";

// mm:ss display for the resend cooldown countdown.
const formatCooldown = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export default function PhoneVerification() {
  const [loading, setLoading] = useState(false);
  const [paramaters] = useSearchParams();
  const navigate = useNavigate();
  const validationKey = paramaters.get("validationKey");
  const registrationKey = paramaters.get("registrationKey");
  const orgId = paramaters.get("orgId");
  const userId = paramaters.get("userId");
  const email = paramaters.get("email");
  const adminType = paramaters.get("type");
  const uniqueIdentifier = paramaters.get("uniqueIdentifier");
  const [phoneNum, setPhoneNum] = useState('')
  const [validationErrors, setValidationErrors] = useState({});
  const yupErrors = {};
  const authBackgroundImage = 'https://res.cloudinary.com/dge9ers9x/image/upload/v1693381778/Making%20View%20Assets/auth_background_texcon.jpg'
  const logoRed =  'https://res.cloudinary.com/dge9ers9x/image/upload/v1693381822/Making%20View%20Assets/MV_logo_red_black_ctm6tn.png'

  const [invitationStatus, setInvitationStatus] = useState(INVITATION_STATUS.LOADING);
  const [resendingInvitation, setResendingInvitation] = useState(false);
  const [invitationResent, setInvitationResent] = useState(false);
  // True when the most recent resend click was blocked by the backend's cooldown (a resend was
  // already sent too recently) - distinct from invitationResent so the UI never claims a fresh
  // email went out when it didn't.
  const [resendBlocked, setResendBlocked] = useState(false);
  // Seconds until another resend will actually send an email (backend-enforced cooldown, see
  // OrganizationManager.ResendInvitationForAdminAsync). Ticks down locally once a resend response
  // reports it, so the button and its countdown reflect the real server-side floor.
  const [cooldownRemaining, setCooldownRemaining] = useState(0);


  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    let userData = storedUserData ? JSON.parse(storedUserData) : {};
  
    // Compare the parameters from the link with the userData in localStorage
    if (
      (validationKey && userData.validationKey !== validationKey) ||
      (registrationKey && userData.registrationKey !== registrationKey) ||
      (email && userData.email !== email) ||
      (phoneNum && userData.phone !== phoneNum) ||
      (adminType && userData.adminType !== adminType) ||
      (orgId && userData.orgId !== orgId) ||
      (userId && userData.userId !== userId ||
      (uniqueIdentifier && userData.uniqueIdentifier !== uniqueIdentifier))
    ) {
      // If there's a difference and the value from the link is not null or undefined, update userData
      userData = {
        validationKey: validationKey || userData.validationKey,
        registrationKey: registrationKey || userData.registrationKey,
        email: email || userData.email,
        phone: phoneNum || userData.phone,
        adminType: adminType || userData.adminType,
        orgId: orgId || userData.orgId,
        userId: userId || userData.userId,
        uniqueIdentifier: uniqueIdentifier || userData.uniqueIdentifier,
      };
  
      localStorage.setItem('userData', JSON.stringify(userData));
    }
  }, [validationKey, registrationKey, email, phoneNum, adminType, orgId, userId, uniqueIdentifier]);
  

  const storedUserData = localStorage.getItem('userData');
  // const userData = storedUserData ? JSON.parse(storedUserData) : null;
  const userData = storedUserData ? JSON.parse(storedUserData) : {
    validationKey,
    registrationKey ,
    email,
    phone: phoneNum,
    adminType,
    orgId ,
    userId ,
    uniqueIdentifier,
  };

  // Check the invitation's status (valid / expired / invalid) before showing any form. Runs once
  // the params we need are known, whether they came from the URL or from a previously-stored
  // userData (e.g. a page refresh mid-flow).
  useEffect(() => {
    const checkOrgId = orgId || userData.orgId;
    const checkUserId = userId || userData.userId;
    const checkEmail = email || userData.email;
    const checkCode = registrationKey || userData.registrationKey;

    if (!checkOrgId || !checkUserId || !checkEmail || !checkCode) {
      setInvitationStatus(INVITATION_STATUS.INVALID);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await apiClient.post(CheckInvitationStatus, {
          orgId: checkOrgId,
          userId: checkUserId,
          email: checkEmail,
          code: checkCode,
        });
        if (!cancelled) {
          setInvitationStatus(response?.data?.data?.status || INVITATION_STATUS.INVALID);
        }
      } catch (err) {
        if (!cancelled) setInvitationStatus(INVITATION_STATUS.INVALID);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isCoolingDown = cooldownRemaining > 0;
  useEffect(() => {
    if (!isCoolingDown) return undefined;
    const intervalId = setInterval(() => {
      setCooldownRemaining((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isCoolingDown]);

  const handleResendInvitation = async () => {
    setResendingInvitation(true);
    try {
      const response = await apiClient.post(ResendExpiredInvitation, {
        orgId: orgId || userData.orgId,
        userId: userId || userData.userId,
        email: email || userData.email,
      });
      const result = response?.data?.data || {};
      setCooldownRemaining(result.cooldownSecondsRemaining || 0);
      if (result.sent) {
        setInvitationResent(true);
        setResendBlocked(false);
        successNote("Ny invitasjon sendt");
      } else {
        // Cooldown blocked this click - no email went out, so don't claim one did. The backend
        // also retires the link the admin currently has in this case (see
        // ResendInvitationForAdminAsync), so any earlier "sent" confirmation is stale too - the
        // countdown is the only way forward now.
        setInvitationResent(false);
        setResendBlocked(true);
        toastErrorNotify("Du ba nylig om en ny lenke. Vent til nedtellingen er ferdig før du kan be om en ny.");
      }
    } catch (err) {
      if (!err.response) {
        toastErrorNotify("Ingen svar fra serveren");
      } else if (err.response.data?.message) {
        toastErrorNotify(err.response.data.message);
      } else {
        toastErrorNotify("Mislyktes");
      }
    } finally {
      setResendingInvitation(false);
    }
  };


 const validationSchema = Yup.object({
number: Yup.string()
.required("Telefonnummeret ditt er påkrevd")
.matches(/^\d{8}$/, "Telefonnummeret må være åtte sifre"),
});

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const data = new FormData(event.currentTarget);
    setPhoneNum(data.get('number'))

    const initialValues = {
      number: data.get("number"),
    };


const params={
  validationKey: validationKey || userData.validationKey ,
  registrationKey:   registrationKey || userData.registrationKey,
  email:  email || userData.email,
  phone: data.get("number"),
}
    try {
      await validationSchema.validate(initialValues, { abortEarly: false });
      //! If validation succeeds, proceed with form submission
     await apiClient.post(SendPhoneValidationOTP, params);
      // Dispatch the action to the Redux store
      // dispatch(setUserData(userData));
      successNote("Verifiseringkode sent");
      navigate("/otpvalidation", { state: { validationKey, registrationKey, email, phone:data.get('number'), adminType, orgId, userId} });
    } catch (err) {
      //! If validation fails, display error messages
      if (err?.inner) {
        err.inner.forEach((e) => {
          yupErrors[e.path] = e.message;
        });
        setValidationErrors(yupErrors);
      } else if (err.response?.data?.message === OTP_LIMIT_REACHED_MESSAGE) {
        setInvitationStatus(INVITATION_STATUS.LIMIT_REACHED);
      }else if(err.response.data.message){
        toastErrorNotify(err.response.data.message);
      } else if(err.response.data.title){
        toastErrorNotify(err.response.data.title);
      } else {
        toastErrorNotify("Registreringen mislyktes");
      }
      console.log(err);
    } finally {
      setLoading(false);
      // console.log('params', params)
    
    }
  };

  return (
    <Grid
      container
      component="main"
      sx={{
        backgroundImage: `url(${authBackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "130vh",
      }}
    >
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            backgroundColor: "rgba(255,255,255, 0.9)",
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 5,
          }}
        >
          <img src={logoRed} alt="Logo" className="logoLogin" />

          {invitationStatus === INVITATION_STATUS.LOADING && (
            <Box sx={{ mt: 4, mb: 2 }}>
              <Loading color={"#2f4874"} size={32} />
            </Box>
          )}

          {invitationStatus === INVITATION_STATUS.INVALID && (
            <>
              <Typography component="h1" variant="h5" sx={{ mt: 2 }}>
                Ugyldig invitasjon
              </Typography>
              <Typography sx={{ mt: 2, fontSize: 15.5, textAlign: "center" }}>
                Denne invitasjonslenken er ugyldig. Dette kan skyldes at den allerede er brukt, at
                administratoren ikke lenger er registrert hos organisasjonen, eller at lenken er
                feil. Ta kontakt med Super-administrator hos din organisasjon for å be om en ny
                invitasjon.
              </Typography>
            </>
          )}

          {invitationStatus === INVITATION_STATUS.SUPERSEDED && (
            <>
              <Typography component="h1" variant="h5" sx={{ mt: 2 }}>
                Denne lenken er ikke lenger i bruk
              </Typography>
              <Typography sx={{ mt: 2, fontSize: 15.5, textAlign: "center" }}>
                Denne registreringslenken er ikke den nyeste vi har sendt deg. Du er fortsatt
                registrert som administrator hos organisasjonen, så du kan be om en ny invitasjon
                under.
              </Typography>
              {invitationResent && (
                <Typography sx={{ mt: 2, fontSize: 14, textAlign: "center", color: "success.main" }}>
                  Ny invitasjon sendt til {email || userData.email}. Sjekk innboksen din (og
                  søppelposten) og bruk lenken i den ferskeste e-posten.
                </Typography>
              )}
              {resendBlocked && (
                <Typography sx={{ mt: 2, fontSize: 14, textAlign: "center", color: "warning.main" }}>
                  Du ba nylig om en ny lenke, så denne lenken er ikke lenger aktiv. Vent til
                  nedtellingen under er ferdig for å be om en ny.
                </Typography>
              )}
              <Button
                className="PrimaryButton"
                fullWidth
                variant="contained"
                disabled={resendingInvitation || cooldownRemaining > 0}
                onClick={handleResendInvitation}
                sx={{ mt: 3, mb: cooldownRemaining > 0 ? 1 : 2 }}
              >
                {resendingInvitation ? <Loading color={"#fff"} size={25} /> : "Send meg en ny invitasjon"}
              </Button>
              {cooldownRemaining > 0 && (
                <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
                  Du kan be om en ny lenke om {formatCooldown(cooldownRemaining)}
                </Typography>
              )}
            </>
          )}

          {invitationStatus === INVITATION_STATUS.EXPIRED && (
            <>
              <Typography component="h1" variant="h5" sx={{ mt: 2 }}>
                Invitasjonen er ikke lenger aktiv
              </Typography>
              <Typography sx={{ mt: 2, fontSize: 15.5, textAlign: "center" }}>
                Denne invitasjonslenken er ikke lenger aktiv - for eksempel fordi den er mer enn
                10 dager gammel. Du er fortsatt registrert som administrator hos organisasjonen,
                så du kan be om en ny invitasjon under.
              </Typography>
              {invitationResent && (
                <Typography sx={{ mt: 2, fontSize: 14, textAlign: "center", color: "success.main" }}>
                  Ny invitasjon sendt til {email || userData.email}. Sjekk innboksen din og åpne
                  lenken i den nye e-posten for å fortsette registreringen.
                </Typography>
              )}
              {resendBlocked && (
                <Typography sx={{ mt: 2, fontSize: 14, textAlign: "center", color: "warning.main" }}>
                  Du ba nylig om en ny lenke, så denne lenken er ikke lenger aktiv. Vent til
                  nedtellingen under er ferdig for å be om en ny.
                </Typography>
              )}
              <Button
                className="PrimaryButton"
                fullWidth
                variant="contained"
                disabled={resendingInvitation || cooldownRemaining > 0}
                onClick={handleResendInvitation}
                sx={{ mt: 3, mb: cooldownRemaining > 0 ? 1 : 2 }}
              >
                {resendingInvitation ? <Loading color={"#fff"} size={25} /> : "Send meg en ny invitasjon"}
              </Button>
              {cooldownRemaining > 0 && (
                <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
                  Du kan be om en ny lenke om {formatCooldown(cooldownRemaining)}
                </Typography>
              )}
            </>
          )}

          {invitationStatus === INVITATION_STATUS.LIMIT_REACHED && (
            <>
              <Typography component="h1" variant="h5" sx={{ mt: 2 }}>
                Du har brukt opp forsøkene dine
              </Typography>
              <Typography sx={{ mt: 2, fontSize: 15.5, textAlign: "center" }}>
                Du har bedt om for mange bekreftelseskoder på denne invitasjonen. Du er fortsatt
                registrert som administrator hos organisasjonen, så du kan be om en ny invitasjon
                under - den nullstiller antall forsøk.
              </Typography>
              {invitationResent && (
                <Typography sx={{ mt: 2, fontSize: 14, textAlign: "center", color: "success.main" }}>
                  Ny invitasjon sendt til {email || userData.email}. Sjekk innboksen din og åpne
                  lenken i den nye e-posten for å fortsette registreringen.
                </Typography>
              )}
              {resendBlocked && (
                <Typography sx={{ mt: 2, fontSize: 14, textAlign: "center", color: "warning.main" }}>
                  Du ba nylig om en ny lenke, så denne lenken er ikke lenger aktiv. Vent til
                  nedtellingen under er ferdig for å be om en ny.
                </Typography>
              )}
              <Button
                className="PrimaryButton"
                fullWidth
                variant="contained"
                disabled={resendingInvitation || cooldownRemaining > 0}
                onClick={handleResendInvitation}
                sx={{ mt: 3, mb: cooldownRemaining > 0 ? 1 : 2 }}
              >
                {resendingInvitation ? <Loading color={"#fff"} size={25} /> : "Send meg en ny invitasjon"}
              </Button>
              {cooldownRemaining > 0 && (
                <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
                  Du kan be om en ny lenke om {formatCooldown(cooldownRemaining)}
                </Typography>
              )}
            </>
          )}

          {invitationStatus === INVITATION_STATUS.VALID && (
            <>
              <Typography component="h1" variant="h5">
              Mobilnummerverifisering
              </Typography>
              <Typography sx={{mt:2, fontSize:16}}>
              Velkommen til Dr. Cloud! <br/> <br/> Skriv inn mobilnummeret ditt for å motta en bekreftelseskode. Bekreft nummeret ditt og fullfør registreringen med denne koden.
              </Typography>
              <Box
                component="form"
                noValidate
                onSubmit={handleSubmit}
                sx={{ mt: 3 }}
              >
                    <TextField
                    sx={{mb:2}}
                      required
                      fullWidth
                      id="number"
                      label="Mobilnummer"
                      name="number"
                      autoComplete="phone"
                      error={Boolean(validationErrors.number)}
                      helperText={validationErrors.number}
                    />
                    {/*<Link*/}
                    {/*  onClick={() =>*/}
                    {/*    navigate("/otpvalidation", {*/}
                    {/*      state: {*/}
                    {/*        validationKey,*/}
                    {/*        registrationKey,*/}
                    {/*        email,*/}
                    {/*        phone: phoneNum || userData.phone,*/}
                    {/*        adminType,*/}
                    {/*        orgId,*/}
                    {/*        userId,*/}
                    {/*      },*/}
                    {/*    })*/}
                    {/*  }*/}
                    {/*  sx={{cursor: 'pointer'}}*/}
                    {/*>Har skrevet inn mobilnummeret før</Link>*/}
                <Button
                  className="PrimaryButton"
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {loading ? <Loading color={"#fff"} size={25} /> : "Verify"}
                </Button>
              </Box>
            </>
          )}

          <Copyright sx={{ mt: 1 }} />
        </Box>
      </Container>
    </Grid>
  );
}
